import { type IamApi } from '@/features/iam/api/iam-api'
import { clearMemoized } from '@/utils/memoize-util'
import { memoizeAsync } from '@/utils/memoize-decorator'
import { TeamMember, type TeamMemberProps } from './TeamMember'
import {
  type GetTeamMemberRequest,
  type TeamMemberResource,
  type PendingTeamMemberResource,
} from '@/features/iam/api/resources/team-member'
import { collection } from '@/infra/collection'
import { type Observable } from 'rxjs'
import { type Member } from '@/features/iam/members/Member'
import type { GetMemberFn } from '@/features/iam'
import { type PrimeFullTeamFn } from './TeamStore'
import { type PrimeTeamRoleFn } from './TeamRoleStore'
import { type GetCurrentMemberFn } from '@/features/iam'
import { Id } from '@/infra/id'
import { type CursorPaged } from '@/infra/paging'
import DataLoader from 'dataloader'
import { getCacheKeyForObject } from '@/utils/object-util'
import {
  PendingTeamMember,
  type PendingTeamMemberProps,
} from './PendingTeamMember'
import { maybeParseDate } from '@/utils/date-util'
import { mapTeamRoleResourceToProps } from './team-role-mappers'
import type { PrimeMemberFn } from '@/features/iam'

/**
 * Props used to prime a team member.
 */
export interface PrimeTeamMemberProps {
  teamId: Id
  workspaceId: Id
  userId: Id
  userPublicId?: Id
}

/**
 * Primes a team member.
 */
export type PrimeTeamMemberFn = (props: PrimeTeamMemberProps) => TeamMember

export interface ListTeamMembersQuery {
  teamId: Id
  inactive?: boolean
  teamRoleId?: Id
  includeTeam?: boolean
  after?: string | null
}

export interface ListPendingTeamMembersParams {
  teamId: Id
  workspaceId: Id
}

export interface ListTeamMembersForWorkspaceMemberParams {
  workspaceId: Id
  userPublicId: Id
  includeTeam?: boolean
  after?: string | null
  limit?: number
}

/**
 * Params for fetching a user's team members. Absent IDs default to the current
 * member's.
 */
export interface ListUserTeamMembersParams {
  workspaceId?: Id
  userPublicId?: Id
}

export interface AddTeamMemberParams {
  workspaceId: Id
  teamId: Id
  teamRoleId: Id
  userPublicId: Id
}

export interface TeamMemberParams {
  teamId: Id
  userPublicId: Id
}

export interface UpdateTeamMemberParams {
  teamRoleId: Id
}

/**
 * Team Member Store.
 */
export class TeamMemberStore {
  /**
   * Team Members collection.
   */
  readonly teamMembers = collection({
    create: () => new TeamMember(),
    // We don't use the TeamMember `id` for anything, always team + user ID.
    getDataId: (data) => `${data.team?.id}/${data.member?.user.id}`,
    getModelId: (model) => `${model.team.id}/${model.member.user.id}`,
  })

  readonly pendingTeamMembers = collection({
    create: () => new PendingTeamMember(),
    // Use the email here to avoid clashing since it seems invite id's could clash
    getDataId: (data) => `${data.team?.id}/${data.email}`,
    getModelId: (model) => `${model.team.id}/${model.email}`,
  })

  private readonly _teamMemberLoader = new DataLoader<
    GetTeamMemberRequest,
    TeamMember | null,
    string
  >(this._fetchMany.bind(this), {
    cacheKeyFn: getCacheKeyForObject,
  })

  constructor(
    private readonly _api: IamApi,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getMember: GetMemberFn,
    private readonly _primeMember: PrimeMemberFn,
    private readonly _primeFullTeam: PrimeFullTeamFn,
    private readonly _primeTeamRole: PrimeTeamRoleFn,
    currentMember$: Observable<Member>,
  ) {
    // The memoized team members belong to the previous member.
    currentMember$.subscribe(() => {
      clearMemoized(this.listUserTeamMembersForWorkspace)
    })
  }

  /**
   * Primes a team member without fetching it.
   */
  primeTeamMember(props: PrimeTeamMemberProps): TeamMember {
    return this.teamMembers.set(
      this._mapTeamMemberResourceToProps({
        user_id: props.userId,
        user_public_id: props.userPublicId,
        team_id: props.teamId,
        workspace_id: props.workspaceId,
      }),
    )
  }

  /**
   * Returns team members for the specified workspace member, defaulting to the current user.
   *
   * @param workspaceId if specified but user ID isn't, returns the current user's teams in the specified workspace
   * @param userId the user ID
   */
  teamMembersForWorkspaceMember(workspaceId?: Id, userId?: Id) {
    const currentMember = this._getCurrentMember()
    const member =
      this._getMember(workspaceId, userId || currentMember.user.id) ??
      currentMember
    return this.teamMembers.filter((x) => x.member === member)
  }

  /**
   * Gets team members by team ID that are in the collection.
   */
  getTeamMembers(teamId: Id) {
    return this.teamMembers.filter((tm) => tm.team.id === teamId)
  }

  /**
   * Gets a local team member by team and user public ID.
   */
  getTeamMember(teamId: Id, userPublicId: Id): TeamMember | undefined {
    return this.teamMembers.find(
      (x) => x.team.id === teamId && x.member.user.publicId === userPublicId,
    )
  }

  /**
   * Gets local team members by team ID.
   */
  forTeam(teamId: Id) {
    const id = Id.assert(teamId)
    return this.teamMembers.filter((x) => x.team.id === id)
  }

  /**
   * Gets local pending team members by team ID.
   */
  pendingForTeam(teamId: Id) {
    const id = Id.assert(teamId)
    return this.pendingTeamMembers.filter((p) => p.team.id === id)
  }

  /**
   * Fetches a team member.
   */
  async fetchTeamMember(
    teamId: Id,
    userId: Id,
    signal?: AbortSignal,
  ): Promise<TeamMember | null> {
    const teamMember = await this._teamMemberLoader.load({
      team_id: Id.assert(teamId),
      user_id: Id.assert(userId),
    })
    signal?.throwIfAborted()
    return teamMember
  }

  /**
   * Fetches the user's team memberships across workspaces. Defaults to the
   * current user.
   */
  @memoizeAsync({
    keySelector: ([params]) => params?.userPublicId || '',
  })
  async listUserTeamMembers(
    params: ListUserTeamMembersParams = {},
    signal?: AbortSignal,
  ): Promise<TeamMember[]> {
    const currentMember = this._getCurrentMember()
    const data = await this._api.teamMembers.listForUser(
      {
        user_id: Id.assert(params.userPublicId || currentMember.user.publicId),
        include_team: true,
      },
      signal,
    )
    return this.teamMembers.set(data.map(this._mapTeamMemberResourceToProps))
  }

  /**
   * Fetches team memberships for the specified workspace member. Defaults to
   * the current member.
   */
  @memoizeAsync({
    keySelector: ([params]) => userTeamMembersForWorkspaceKey(params),
  })
  async listUserTeamMembersForWorkspace(
    params: ListUserTeamMembersParams = {},
    signal?: AbortSignal,
  ): Promise<TeamMember[]> {
    const currentMember = this._getCurrentMember()
    const data = await this._api.teamMembers.listForWorkspaceMember(
      {
        workspace_id: Id.assert(
          params.workspaceId || currentMember.workspace.id,
        ),
        user_id: Id.assert(params.userPublicId || currentMember.user.publicId),
        include_team: true,
      },
      signal,
    )
    return this.teamMembers.set(data.map(this._mapTeamMemberResourceToProps))
  }

  /**
   * Fetches team memberships for the specified workspace member, bypassing
   * the cache of {@link listUserTeamMembersForWorkspace}.
   */
  async refetchUserTeamMembersForWorkspace(
    params: ListUserTeamMembersParams = {},
    signal?: AbortSignal,
  ): Promise<TeamMember[]> {
    clearMemoized(
      this.listUserTeamMembersForWorkspace,
      userTeamMembersForWorkspaceKey(params),
    )
    return this.listUserTeamMembersForWorkspace(params, signal)
  }

  /**
   * Lists the members of a team.
   */
  async listTeamMembers(
    query: ListTeamMembersQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<TeamMember>> {
    const response = await this._api.teamMembers.list(
      {
        team_id: Id.assert(query.teamId),
        inactive: query.inactive,
        teamRoleId: query.teamRoleId,
        include_team: query.includeTeam,
        after: query.after,
        limit: 100,
      },
      signal,
    )
    return {
      data: this.teamMembers.set(
        response.data.map(this._mapTeamMemberResourceToProps),
      ),
      next: response.cursor,
      total: response.total,
    }
  }

  /**
   * Lists the team memberships of a workspace member by user public ID.
   */
  async listTeamMembersForWorkspaceMember(
    params: ListTeamMembersForWorkspaceMemberParams,
    signal?: AbortSignal,
  ): Promise<TeamMember[]> {
    const result = await this._api.teamMembers.listForWorkspaceMemberByPublicId(
      {
        user_public_id: Id.assert(params.userPublicId),
        workspace_id: Id.assert(params.workspaceId),
        include_team: params.includeTeam,
        after: params.after,
        limit: params.limit,
      },
      signal,
    )
    return this.teamMembers.set(result.map(this._mapTeamMemberResourceToProps))
  }

  /**
   * Lists the pending invitations to a team.
   */
  async listPendingTeamMembers(
    params: ListPendingTeamMembersParams,
    signal?: AbortSignal,
  ) {
    const result = await this._api.teamMembers.listPending(
      {
        team_id: Id.assert(params.teamId),
        workspace_id: Id.assert(params.workspaceId),
      },
      signal,
    )
    return this.pendingTeamMembers.set(
      result.data.map(this._mapPendingTeamMemberResourceToProps),
    )
  }

  /**
   * Adds a workspace member to a team. Requires workspace permissions, not something
   * that a team admin can do on their own.
   */
  async addTeamMemberByPublicId(params: AddTeamMemberParams) {
    return this._api.teamMembers
      .createByPublicId({
        // The API names it user ID, but expects the public ID.
        user_id: Id.assert(params.userPublicId),
        team_id: Id.assert(params.teamId),
        team_role_id: Id.assert(params.teamRoleId),
        workspace_id: Id.assert(params.workspaceId),
      })
      .then((data) =>
        this.teamMembers.set(this._mapTeamMemberResourceToProps(data)),
      )
  }

  /**
   * Updates a team member.
   */
  async updateTeamMember(
    params: TeamMemberParams,
    payload: UpdateTeamMemberParams,
  ) {
    return this._api.teamMembers
      .update(Id.assert(params.teamId), Id.assert(params.userPublicId), {
        team_role_id: Id.assert(payload.teamRoleId),
      })
      .then((data) =>
        this.teamMembers.set(this._mapTeamMemberResourceToProps(data)),
      )
  }

  /**
   * Deletes a team member.
   */
  async deleteTeamMember(params: TeamMemberParams) {
    const request = {
      team_id: Id.assert(params.teamId),
      user_id: Id.assert(params.userPublicId),
    }
    await this._api.teamMembers.delete(request)
    const teamMember = this.getTeamMember(request.team_id, request.user_id)
    if (teamMember) this.teamMembers.remove(teamMember)
  }

  /**
   * Fetches many team members. Uses DataLoader.
   */
  private async _fetchMany(
    keys: readonly GetTeamMemberRequest[],
  ): Promise<(TeamMember | null)[]> {
    return this._api.teamMembers
      .getMany(keys)
      .then((x) =>
        x.map((v) =>
          v
            ? this.teamMembers.set(this._mapTeamMemberResourceToProps(v))
            : null,
        ),
      )
  }

  /**
   * Filters team members by user public Id and Team Id.
   */
  filterTeamMembers(publicId: Id, teamId: Id) {
    return this.teamMembers.filter(
      (m) => m.team.id === teamId && m.member.user.publicId === publicId,
    )
  }

  private readonly _mapTeamMemberResourceToProps = (
    resource: Partial<TeamMemberResource>,
  ): Partial<TeamMemberProps> => ({
    inactive: resource.inactive,
    workspaceId: resource.workspace_id,
    dateCreated: maybeParseDate(resource.date_created),
    dateModified: maybeParseDate(resource.date_modified),
    teamRole: resource.team_role
      ? this._primeTeamRole(mapTeamRoleResourceToProps(resource.team_role))
      : undefined,
    team: this._primeFullTeam({ id: resource.team_id, ...resource.team }),
    member: this._primeMember({
      workspaceId: resource.workspace_id,
      userId: resource.user_id,
      userPublicId: resource.user_public_id,
    }),
  })

  private readonly _mapPendingTeamMemberResourceToProps = (
    resource: PendingTeamMemberResource,
  ): Partial<PendingTeamMemberProps> => ({
    workspaceId: resource.workspace_id,
    dateCreated: maybeParseDate(resource.date_created),
    dateModified: maybeParseDate(resource.date_modified),
    email: resource.email,
    team: this._primeFullTeam({
      id: resource.team_id,
      workspace_id: resource.workspace_id,
    }),
    teamRole: this._primeTeamRole(
      mapTeamRoleResourceToProps({
        id: resource.team_role_id,
        workspace_id: resource.workspace_id,
      }),
    ),
    expires: maybeParseDate(resource.expires),
  })
}

function userTeamMembersForWorkspaceKey(params?: ListUserTeamMembersParams) {
  return `${params?.workspaceId}/${params?.userPublicId}`
}
